/**
 * Adds items to Shopify cart with optional cart clearing, discount code, redirect, and callbacks.
 * Supports dynamic conditional redirect logic via getRedirectUrl callback.
 *
 * @param {Object} options
 * @param {Array} options.items - Array of items in Shopify add format [{id, quantity}, ...]
 * @param {boolean} [options.clearCart=false] - Whether to clear cart first before adding
 * @param {string|null} [options.discountCode=null] - Discount code to apply (optional)
 * @param {string|null} [options.redirectUrl=null] - URL to redirect to after success (optional)
 * @param {function|null} [options.getRedirectUrl=null] - Callback returning redirect URL (optional)
 * @param {function|null} [options.onSuccess=null] - Callback on successful add (optional)
 * @param {function|null} [options.onError=null] - Callback on error (optional)
 *
 * @returns {Promise<Object|undefined>} Resolves with add response data or undefined on validation failure.
 *
 * Usage examples:
 *
 * 1. Simple add with discount code and default redirect:
 * addToCart({
 *   items: [{ id: 123456, quantity: 1 }],
 *   discountCode: 'SAVE20',
 * });
 *
 * 2. Clear cart before adding, custom redirect URL:
 * addToCart({
 *   items: [{ id: 123456, quantity: 1 }],
 *   clearCart: true,
 *   redirectUrl: '/thank-you',
 * });
 *
 * 3. Use dynamic redirect based on sessionStorage value:
 * addToCart({
 *   items: [{ id: 123456, quantity: 1 }],
 *   clearCart: true,
 *   getRedirectUrl: () => {
 *     if (sessionStorage.getItem('david') === '56') {
 *       return '/discount/DAVID56?redirect=/checkout';
 *     }
 *     return `/checkout?discount=USA,SAVE-LPC`;
 *   }
 * });
 *
 * 4. Add success and error callbacks:
 * addToCart({
 *   items: [{ id: 123456, quantity: 1 }],
 *   onSuccess: data => console.log('Add success:', data),
 *   onError: err => console.error('Add error:', err),
 * });
 */
async function addToCart({
  items,
  clearCart = false,
  discountCode = null,
  redirectUrl = null,
  getRedirectUrl = null,
  onSuccess = null,
  onError = null,
  cartAttributes = {}, // <— new param
} = {}) {
  // Validate items payload with optional selling_plan
  if (!Array.isArray(items) || items.length === 0) {
    console.error('[addToCart] Invalid "items": must be a non-empty array.');
    if (typeof onError === 'function') onError(new Error('Invalid items array'));
    return;
  }

  for (const item of items) {
    if (
      typeof item !== 'object' ||
      typeof item.id !== 'number' ||
      typeof item.quantity !== 'number' ||
      item.quantity < 1 ||
      (item.hasOwnProperty('selling_plan') && typeof item.selling_plan !== 'number')
    ) {
      console.error('[addToCart] Invalid item in "items": each must have numeric id and quantity >=1; optional selling_plan must be numeric if present.', item);
      if (typeof onError === 'function') onError(new Error('Invalid item format'));
      return;
    }
  }
  try {
    if (clearCart) {
      //   console.log('[addToCart] Clearing cart...');
      const clearResponse = await fetch('/cart/clear.js', { method: 'POST' });
      if (!clearResponse.ok) throw new Error('Failed to clear cart');
      //   console.log('[addToCart] Cart cleared');
    }

    // set cart-level attributes if provided
    if (Object.keys(cartAttributes).length) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: cartAttributes }),
      });
    }

    // console.log('[addToCart] Adding items to cart...', items);
    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!addResponse.ok) throw new Error('Failed to add items to cart');

    const addData = await addResponse.json();
    // console.log('[addToCart] Items added successfully:', addData);

    if (typeof onSuccess === 'function') onSuccess(addData);

    // Determine redirect URL with priority:
    // 1. getRedirectUrl callback (if provided)
    // 2. redirectUrl param
    // 3. discountCode param redirect
    let finalRedirectUrl = null;

    if (typeof getRedirectUrl === 'function') {
      finalRedirectUrl = getRedirectUrl(addData);
    }
    if (!finalRedirectUrl) {
      if (redirectUrl) finalRedirectUrl = redirectUrl;
      else if (discountCode) finalRedirectUrl = `/checkout?discount=${encodeURIComponent(discountCode)}`;
    }

    if (finalRedirectUrl) {
      //   console.log('[addToCart] Redirecting to:', finalRedirectUrl);
      window.location.href = finalRedirectUrl;
    } else {
      console.log('[addToCart] No redirect configured, stay on page');
    }

    return addData;
  } catch (error) {
    console.error('[addToCart] Error:', error);
    if (typeof onError === 'function') onError(error);
    else console.error('There was an error adding items to the cart.');
    throw error;
  }
}
